import { Button as AntButton, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Copy, Undo2 } from "lucide-react";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Routes } from "../../../../../AppRoutes";
import { FontFa } from "../../../../../components";
import BusinessTicketStatus from "../../../../../components/status/businessTicketStatus";
import { RequestStatus, requestStatus } from "../../../../../constants";
import { DateHelper } from "../../../../../helper";
import { TableHelpers } from "../../../../../helper/TableHelper";
import { ticketTypeRequest } from "../../companyRequestContants/companyRequestContants";
import useExportDocxFile from "../../request-vehicle/TemplateDocx/useExportDocxFile";
import UploadFile from "../components/upload-file";

const getApprovalProcess = (data: any) => {
  if (data.eRequestStatus !== RequestStatus.submitted) return "";

  const steps = JSON.parse(data.eSteps || "[]") as any;

  const currentStepIndex = steps.findIndex((step: any) =>
    step.every((item: any) => !item.isRejected && !item.isApproved),
  );
  const currentStep = steps[currentStepIndex];

  if (!currentStep || !currentStep.length) return "";

  const mainApproval = currentStep.find((item: any) => item.isRequired) || currentStep[0];

  return `${currentStepIndex}/${steps.length} chờ ${mainApproval.name} duyệt`;
};

export const useBusinessTicketsColumn = () => {
  const history = useHistory();

  const [currentItem, setCurrentItem] = useState<any>();
  const { exportForm } = useExportDocxFile({ item: currentItem, docType: 2 });

  const handleSubmit = (item: any, isReroute: boolean = false) => {
    exportForm(item, isReroute);
  };

  const handleReturnTicket = (e: any, record: any) => {
    e.stopPropagation();
    e.preventDefault();

    history.push({
      pathname: Routes.createProcedureCollaborateTicket.path.replace(":id", 0),
      search: `?return-ticket=${record.id}`,
    });
  };

  const handleCopyTicket = (e: any, record: any) => {
    e.stopPropagation();
    e.preventDefault();

    history.push(Routes.duplicateProcedureCollaborateTicket.path.replace(":id", record.id));
  };

  const columns: ColumnsType<any> = [
    {
      title: "Xuất tài liệu",
      dataIndex: "export",
      key: "export",
      align: "center" as const,
      width: 120,
      fixed: true,
      render: (text: string, record: any) => (
        <>
          <Button
            title={"Xuất phiếu yêu cầu xuất vé"}
            className="mr-2"
            variant="outline-info"
            onClick={(e: any) => {
              e.stopPropagation();
              setCurrentItem(record);
              handleSubmit(record);
            }}
          >
            <FontFa name="faFileWord" size={"xs"} />
          </Button>
          <Button
            title={"Xuất phiếu yêu cầu hoàn vé"}
            className=""
            variant="outline-warning"
            onClick={(e: any) => {
              e.stopPropagation();
              setCurrentItem(record);
              handleSubmit(record, true);
            }}
          >
            <FontFa name="faFileWord" size={"xs"} />
          </Button>
        </>
      ),
    },
    {
      title: "-",
      dataIndex: "action",
      key: "action",
      align: "center" as const,
      fixed: true,
      render: (_, record: any) => (
        <Space direction="horizontal" size={8}>
          <Tooltip title="Sao chép">
            <AntButton
              style={{
                height: 35.75,
              }}
              icon={<Copy size={14} />}
              onClick={(e) => handleCopyTicket(e, record)}
              disabled={![requestStatus.rejected, requestStatus.canceled].includes(record.eRequestStatus)}
            />
          </Tooltip>
          <Tooltip title="Hoàn vé">
            <AntButton
              style={{
                height: 35.75,
              }}
              icon={<Undo2 size={14} />}
              onClick={(e) => handleReturnTicket(e, record)}
              disabled={record.eTicketType === "refund" || record.eRequestStatus !== requestStatus.completed}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Số phiếu",
      dataIndex: "id",
      key: "id",
      align: "center" as const,
      width: "100px",
      fixed: true,
      ...TableHelpers.searchInput({ dataIndex: "id" }),
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdDateTime",
      key: "createdDateTime",
      render: (text: string) => DateHelper.formatDateToString(text),
      align: "center" as const,
      width: "120px",
      ...TableHelpers.searchRangePicker({ dataIndex: "createdDateTime" }),
    },
    {
      title: "Người tạo",
      dataIndex: "createdByName",
      key: "createdByName",
      width: "200px",
      ...TableHelpers.searchInput({ dataIndex: "createdByName" }),
    },
    {
      title: "Yêu cầu",
      dataIndex: "eTicketType",
      key: "eTicketType",
      render: (text: string) => (
        <span className={text !== "refund" ? "flag success" : "flag danger"}>
          {text && (ticketTypeRequest.find((o) => o.value === text)?.label || "")}
        </span>
      ),
      width: "180px",
      filters: [
        { text: "Cấp vé và giấy đi đường", value: "full" },
        { text: "Chỉ cấp vé", value: "ticket" },
        { text: "Chỉ cấp giấy đi đường", value: "passport" },
        { text: "Hoàn vé", value: "refund" },
      ],
      onFilter: (status: any, record) => record.eTicketType.indexOf(status) === 0,
    },
    {
      title: "Đơn vị",
      dataIndex: "DepartmentScope",
      key: "DepartmentScope",
      render: (text: string) => text?.split("|")?.at(-1) || "",
      ...TableHelpers.searchInput({ dataIndex: "DepartmentScope" }),
    },
    {
      title: "Thời gian",
      dataIndex: "eWorkingTime",
      key: "eWorkingTime",
      render: (text: string, data: any) =>
        `${DateHelper.formatDateToString(text)} - ${DateHelper.formatDateToString(data.eFinishTime)}`,
      align: "center" as const,
      ...TableHelpers.searchRangePicker({ dataIndex: "eWorkingTime" }),
    },
    {
      title: "Bổ sung tài liệu",
      dataIndex: "upload",
      key: "upload",
      align: "center" as const,
      width: 140,
      render: (_: string, record: any) => <UploadFile data={record} />,
    },
    {
      title: "Trạng thái",
      dataIndex: "eRequestStatus",
      key: "eRequestStatus",
      render: (text: string, data: any) => {
        const approvalName = getApprovalProcess(data);

        return (
          <div className="flex flex-col gap-1">
            <BusinessTicketStatus status={text} />
            {approvalName && <span className="text-xs text-gray-500">{approvalName}</span>}
          </div>
        );
      },
      filters: [
        {
          text: "Đang soạn thảo",
          value: requestStatus.draft,
        },
        {
          text: "Đang đợi duyệt",
          value: requestStatus.submitted,
        },
        {
          text: "Đã duyệt",
          value: requestStatus.completed,
        },
        {
          text: "Từ chối",
          value: requestStatus.rejected,
        },
      ],
      onFilter: (status: any, record) => record.eRequestStatus.indexOf(status) === 0,
    },
    {
      title: "Mô tả",
      dataIndex: "eDescription",
      key: "eDescription",
      ...TableHelpers.searchInput({ dataIndex: "eDescription" }),
    },
  ];
  return { columns };
};
